import { readConsent, subscribe } from "@/app/consent/consent-store";
import { getRuntimeGAMeasurementId } from "@/config/auth";
import { useEffect } from "react";

const ATTR = "data-ezyhub-gtag";
const GTAG_GLOBAL = "gtag" as const;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Google Consent Mode v2 integration.
 *
 * The gtag loader is injected exactly once with consent defaults of "denied" for every
 * category (`analytics_storage`, `ad_storage`, `ad_user_data`, `ad_personalization`). This
 * gives us two things:
 *   1. LGPD/GDPR compliance — no tracking cookies or identifiers are written before the user
 *      gives consent via the banner.
 *   2. "Cookieless pings" — while in denied state, gtag still sends aggregated pings that
 *      Google can use for modeled conversions and basic reporting without storing anything
 *      persistent on the device.
 *
 * Once the user decides, we subscribe to the consent store and call `gtag('consent','update')`
 * with the resolved values. Every subsequent change (preferences dialog) updates Google in
 * real time without a page reload.
 *
 * When no measurement ID is configured (local dev), everything is skipped and the consent
 * subscription is a no-op.
 */
export function GoogleAnalytics() {
  useEffect(() => {
    const id = getRuntimeGAMeasurementId();
    if (!id) return;

    if (!document.querySelector(`script[${ATTR}="${id}-bootstrap"]`)) {
      const bootstrap = document.createElement("script");
      bootstrap.setAttribute(ATTR, `${id}-bootstrap`);
      bootstrap.textContent = `
window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
window.gtag = gtag;
gtag('consent', 'default', {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
  security_storage: 'granted',
  wait_for_update: 500
});
gtag('js', new Date());
gtag('config', ${JSON.stringify(id)}, {
  anonymize_ip: true,
  allow_google_signals: false,
  allow_ad_personalization_signals: false
});
`;
      document.head.appendChild(bootstrap);

      const loader = document.createElement("script");
      loader.async = true;
      loader.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
      loader.setAttribute(ATTR, `${id}-loader`);
      document.head.appendChild(loader);
    }

    const apply = (analytics: boolean, marketing: boolean) => {
      const push = window[GTAG_GLOBAL];
      if (typeof push !== "function") return;
      push("consent", "update", {
        analytics_storage: analytics ? "granted" : "denied",
        ad_storage: marketing ? "granted" : "denied",
        ad_user_data: marketing ? "granted" : "denied",
        ad_personalization: marketing ? "granted" : "denied",
      });
    };

    const current = readConsent();
    if (current.decided) apply(current.analytics, current.marketing);

    return subscribe((next) => {
      if (next.decided) apply(next.analytics, next.marketing);
    });
  }, []);

  return null;
}
